<template>

  <alert-item
      id="alertPC"
      class="alert-warning"
      v-if="managedAsPrimaryContact && myRole !== 'userAdmin'"
      :closeable="false">
    <span v-html="$t('adm.page.userDetail.infoPrimaryContact')"/>
  </alert-item>

  <form>

    <form-item-group
        container-class="mb-4"
        :required="true"
        :label="$t('adm.label.email')"
        label-for="userEmail"
        :validation="v$.form.email"
        :is-disabled="true">
      <input
          type="email"
          class="form-control"
          id="userEmail"
          v-model="v$.form.email.$model"
          @input="$emit('update:email', $event)"
          aria-label="email"
          :disabled="true">
    </form-item-group>

    <div class="row mb-4">
      <form-item-group
          container-class="col"
          :required="true"
          :label="$t('adm.label.firstname')"
          label-for="userFirstname"
          :validation="v$.form.firstname"
          :is-disabled="isReadOnly">
        <input
            type="text"
            class="form-control"
            id="userFirstname"
            v-model="v$.form.firstname.$model"
            @input="$emit('update:firstname', form.firstname)"
            aria-label="first name"
            :disabled="isReadOnly"/>
      </form-item-group>
      <form-item-group
          container-class="col"
          :required="true"
          :label="$t('adm.label.lastname')"
          label-for="userLastname"
          :validation="v$.form.lastname"
          :is-disabled="isReadOnly">
        <input
            type="text"
            class="form-control"
            id="userLastname"
            v-model="v$.form.lastname.$model"
            @input="$emit('update:lastname', form.lastname)"
            aria-label="last name"
            :disabled="isReadOnly">
      </form-item-group>
    </div>

    <div class="row mb-4">
      <form-item-group
          container-class="col"
          :validation="v$.form.preferredLanguage"
          :label="$t('adm.label.language')"
          label-for="userLang"
          :is-disabled="isReadOnly">
        <multiselect
            id="userLang"
            v-model="v$.form.preferredLanguage.$model"
            :options="languages"
            value-prop="locale"
            label="name"
            mode="single"
            @select="$emit('update:lang', form.preferredLanguage)"
            @deselect="$emit('update:lang', form.preferredLanguage)"
            :close-on-select="true"
            :can-deselect="false"
            :can-clear="false"
            :placeholder="$t('adm.multiselect.placeholder')"
            :multiple-label="msMultipleLabel"
            :disabled="isReadOnly"/>
      </form-item-group>

      <form-item-group
          container-class="col"
          :required="true"
          :label="$t('adm.label.organization')"
          label-for="userOrg"
          :validation="v$.form.organization"
          :is-disabled="isReadOnly || $arraySize(managedAsPrimaryContact) > 0">
        <select1-organization
            id="userOrg"
            v-model="v$.form.organization.$model"
            :init-org-data="orgData"
            @update:model-value="$emit('update:organization', $event)"
            :disabled="isReadOnly || $arraySize(managedAsPrimaryContact) > 0"/>
      </form-item-group>
    </div>

    <div class="row mb-4">
      <form-item-group
          container-class="col"
          :required="true"
          :label="$t('adm.label.adminLevel')"
          label-for="adminLevel"
          :validation="v$.form.adminLevel"
          :is-disabled="!canEditAdminRole">
        <multiselect
            id="adminLevel"
            v-model="v$.form.adminLevel.$model"
            :options="adminLevelOptions"
            value-prop="k"
            label="v"
            mode="single"
            :close-on-select="true"
            :can-deselect="false"
            :can-clear="false"
            @select="$emit('update:admin-level', $event)"
            :disabled="!canEditAdminRole"/>
      </form-item-group>
      <form-item-group
          container-class="col"
          :required="true"
          :label="$t('adm.label.managedOrganizations')"
          label-for="managedOrg"
          :validation="v$.form.managedOrganizations"
          :is-disabled="isReadOnly || !canEditManagedOrg">
        <select-multi-organizations
            id="managedOrg"
            :selected="v$.form.managedOrganizations.$model"
            :options-provider="fetchManagedOrg"
            @update:model-value="$emit('update:managed-organizations', $event)"
            :disabled="isReadOnly || !canEditManagedOrg"
            :is-item-read-only="isPCOfOrganization"/>
      </form-item-group>
    </div>

    <div class="row mb-4" v-if="showPIN">
      <form-item-group
          container-class="col"
          :required="isPINRequired"
          :label="$t('adm.label.pin')"
          label-for="pin"
          :validation="v$.form.pin.value"
          :is-disabled="isReadOnly"
          v-if="showPIN">
        <input type="text"
               class="form-control"
               placeholder="###.###.###-##"
               id="pin"
               v-model="v$.form.pin.value.$model"
               @input="$emit('update:pin', form.pin)"
               :aria-label="$t('adm.label.pin')"
               :disabled="isReadOnly"/>
      </form-item-group>
      <form-item-group container-class="col"
                       :required="isPINRequired"
                       :label="$t('adm.label.birthdate')"
                       label-for="birthdate"
                       :validation="v$.form.birthdate"
                       :is-disabled="isReadOnly"
                       v-if="showPIN">
        <VueDatePicker v-model="form.birthdate"
                       text-input auto-apply hide-input-icon
                       :enable-time-picker="false"
                       :locale="i18n.global.locale"
                       :disabled="isReadOnly"
                       start-date="2000-01-01"
                       :disabled-dates="(d) => d > new Date()"
                       :format="'yyyy-MM-dd'" placeholder="YYYY-MM-DD"
                       :ui="{input: 'form-control'}" position="left"
                       @update:model-value="$emit('update:birthdate', toISODate(form.birthdate))"/>
      </form-item-group>
    </div>

    <div class="row mb-4" v-if="showRole">
      <form-item-group
          class="ms-stihl"
          container-class="col"
          :validation="v$.form.roles"
          :label="$t('adm.label.role')"
          label-for="userRole"
          :required="isRoleRequired"
          :is-disabled="isReadOnly"
          v-if="showRole">
        <multiselect
            id="userRole"
            v-model="v$.form.roles.$model"
            :options="roleOptions"
            value-prop="value"
            label="name"
            mode="multiple"
            @select="$emit('update:roles', form.roles)"
            @deselect="$emit('update:roles', form.roles)"
            :close-on-select="false"
            :can-deselect="true"
            :hide-selected="false"
            :can-clear="false"
            :placeholder="$t('adm.multiselect.placeholder')"
            :multiple-label="msMultipleLabel"
            :required="isRoleRequired"
            :disabled="isReadOnly">
          <template #option="item">
            <label
                class="form-check stihl-ms-menu-item"
                v-tooltip="item.option.descr"
                data-bs-placement="right">
              <input
                  type="checkbox"
                  tabindex="-1"
                  class="pe-none"
                  role="option"
                  :checked="item.isSelected(item.option)"
                  @click.stop.prevent="">
              <span class="stihl-ms-item-text">{{ item.option.name }}</span>
            </label>
          </template>
        </multiselect>
      </form-item-group>
      <div class="stihl-input-group col"/>
    </div>

  </form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { arraySize, contains, DisplayableOrg, toISODate } from '@/components/utils';
import Backend, { adminLevels, BackendError, OrganizationDetails } from '@/components/backend';
import Multiselect from '@vueform/multiselect';
import { mapState } from 'pinia';
import useVuelidate from '@vuelidate/core';
import {
  cpfNotInUse,
  maxLength,
  minLength,
  mustBeAdult,
  required,
  validAdminLevel,
  validEmail,
  validPIN,
} from '@/components/validators';
import FormItemGroup from '@/components/ui/FormItemGroup.vue';
import Select1Organization from '@/components/ui/Select1Organization.vue';
import { Logger } from 'oidc-client-ts';
import AlertItem from '@/components/ui/AlertItem.vue';
import { i18n, languageOptions, REGEX_LOCALE } from '@/lang';
import useAppStore from '@/components/store';
import { requiredIf } from '@vuelidate/validators';
import SelectMultiOrganizations, { toDisplayable } from '@/components/ui/SelectMultiOrganizations.vue';
import { UserForm } from './index.vue';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

type RoleOption = { value: string, name: string, descr: string };

export default defineComponent({
  name: 'UserDetails',
  components: {
    SelectMultiOrganizations,
    AlertItem,
    FormItemGroup,
    Multiselect,
    Select1Organization,
    VueDatePicker,
  },
  emits: ['update:email', 'update:firstname', 'update:lastname', 'update:lang', 'update:roles',
    'update:organization', 'update:admin-level', 'update:managed-organizations', 'update:pin',
    'update:birthdate'],
  // expose: ['isValid'/* , 'changes' */], // fixme: uncomment (bug in this versions of vue)
  props: {
    crmId: { type: String, default: null },
    userForm: { type: Object as PropType<UserForm>, required: true },
    orgData: { type: Object as PropType<OrganizationDetails>, default: null },
    managedAsPrimaryContact: { type: Array as PropType<string[]>, default: null },
    showErrors: { type: Boolean, default: false },
  },
  data() {
    return {
      form: this.userForm, // references the object from parent
      roleOptions: [] as RoleOption[], // active business-roles (depend on organization)
    };
  },
  computed: {
    ...mapState(useAppStore, {
      myRole: 'getMyAdminRole',
      canEditUser: 'canEditUser',
    }),
    i18n: () => (i18n),
    isReadOnly(): boolean {
      return !this.canEditUser(this.form.adminLevel);
    },
    canEditAdminRole(): boolean {
      return this.canEditUser(this.form.adminLevel) && arraySize(this.managedAsPrimaryContact) === 0;
    },
    canEditManagedOrg(): boolean {
      return this.canEditUser(this.form.adminLevel)
          && (this.form.adminLevel === 'userAdmin' || this.form.adminLevel === 'orgSuperAdmin');
    },
    adminLevelOptions(): Record<string, any> {
      // a SupportAdmin can edit ony 'none' & 'userAdmin'
      const levels = adminLevels.filter((r) => this.canEditUser(r));
      return levels.map((lvl) => ({
        k: lvl,
        v: this.$t(`adm.label.adminRoles.${lvl}`),
        disabled: this.myRole !== 'superAdmin' && lvl > 'userAdmin',
      }));
    },
    languages() {
      return languageOptions(this.$t);
    },
    showRole(): boolean {
      // role not visible - if not configured or no roles available
      if (!window.APP_CONFIG?.roles?.show || arraySize(this.roleOptions) === 0) return false;

      // only visible for specific business types (if any configured)
      const onlyBT = window.APP_CONFIG?.roles?.onlyBusinessTypes;
      const myBT = this.orgData?.businessType;
      // return !onlyBT || (!!myBT && contains(onlyBT, myBT));
      const x = !onlyBT || (!!myBT && contains(onlyBT, myBT.toString()));
      Logger.info('EditUser', `showRole: ${x} (only:${onlyBT},my:${myBT})`);
      return x;
    },
    isRoleRequired(): boolean {
      return this.myRole === 'userAdmin' && this.showRole;
    },
    showPIN(): boolean {
      return contains(this.orgData?.countries, 'BRA');
    },
    isPINRequired(): boolean {
      return this.myRole !== 'superAdmin' && this.showPIN;
    },
  },

  methods: {
    toISODate,

    isValid(): boolean {
      this.v$.$touch();
      if (this.v$.$invalid) {
        Logger.error('validation failed', this.v$?.$errors);
        return false;
      }
      return true;
    },

    isPCOfOrganization(key: string): boolean {
      Logger.debug(`isPCOfOrganization ${key} (this.managed as PC: ${this.managedAsPrimaryContact})`);
      return this.managedAsPrimaryContact?.includes(key) ?? false;
    },

    async fetchManagedOrg(): Promise<DisplayableOrg[]> {
      if (arraySize(this.userForm.managedOrganizations) > 0) {
        const api = await Backend.validateToken();
        return api.findOrganizations({
          $filter: `admins == '${this.userForm.id}'`,
          $select: 'id,name,crmNo,state',
        }).then((r) => toDisplayable(r.data));
      }
      return [];
    },

    msMultipleLabel(value?: []) {
      // Logger.debug(`[UserDetails] msMultipleLabel`, value)
      const items = value?.length ?? 0;
      return this.$t('adm.multiselect.selected', items);
    },

    async loadActiveRoles() {
      Backend.validateToken().then((api) => api.getActiveRolesV3(this.userForm.organization, this.i18n.global.locale).then((r) => {
        const availableRoles = r.data;

        if (availableRoles) {
          for (let i = 0; i < availableRoles.length; i++) {
            const role = availableRoles[i];
            this.roleOptions.push({
              value: role.id,
              name: role.name,
              descr: role.description,
            });
          }
        }
        const selection: string[] | undefined = this.userForm.roles;
        // sort by selection & translation
        this.roleOptions.sort((a: RoleOption, b: RoleOption) => {
          const aIsSel = contains(selection, a.value) ? -1 : 1;
          const bIsSel = contains(selection, b.value) ? -1 : 1;
          const diffSel = aIsSel - bIsSel;
          if (diffSel !== 0) return diffSel;
          if (a.name < b.name) return -1;
          return a.name === b.name ? 0 : 1;
        });
      }));
    },

    handleError(e: BackendError) {
      if (e.response?.status === 400) {
        Logger.error('EditUser', e?.restError);
        this.$router.push('/error');
      } else if (e.response?.status === 404) {
        Logger.error('EditUser', 'NOT FOUND');
        this.$router.push('/not-found');
      } else if (e.response?.status === 403) {
        Logger.error('EditUser', '\'Forbidden\' received from backend.');
        this.$router.push('/access-denied');
      } else {
        Backend.errorMessage(e, this.$t);
      }
    },
  },

  async mounted() {
    await this.loadActiveRoles();
  },

  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        email: { required, maxLength: maxLength(254), validEmail },
        firstname: { required, minLength: minLength(1), maxLength: maxLength(100) },
        lastname: { required, minLength: minLength(1), maxLength: maxLength(100) },
        roles: { required: requiredIf(this.isRoleRequired), $autoDirty: true },
        organization: { required },
        preferredLanguage: { regex: REGEX_LOCALE },
        adminLevel: { validAdminLevel },
        managedOrganizations: {},
        pin: { value: { required: requiredIf(this.isPINRequired), validPIN, cpfNotInUse } },
        birthdate: { required: requiredIf(this.isPINRequired), mustBeAdult },
      },
    };
  },
});
</script>
